import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context/hooks";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { ParkingProductType } from "../../models";
import { deleteEntryRightDelegees } from "../../services/entryRights.service";
import { useAppDispatch, useAppSelector } from "../../store";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import Warning from "./Warning";
import { isEntryRightsMyProductsRowKey, isEntryRightsMyProductsRowKeyArray } from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";

type FormOptions = {
    warning: string;
    warningDescription: string;
    revocationEmailText: string;
};

interface IFormProps {
    onRefresh: () => void;
    options: FormOptions;
    productType?: ParkingProductType;
}

const BatchDeleteDelegeeWarning: React.FC<IFormProps> = ({ onRefresh, productType, options }) => {
    const loading = useAppSelector(s => s.parkingProducts.loading);
    const parkingDelegees = useAppSelector(s => s.parkingProducts.delegees);
    const { t } = useTranslation("globals");
    const dispatch = useAppDispatch();

    const { appState } = useAppContext();

    const { fleetManagerState, setSelectedParkingRightIds, toggleBatchDeleteDelegationsWarning } =
        useFleetManagerContext();

    const [sendRevocationConfirmationEmail, setSendRevocationConfirmationEmail] = useState(true);

    const shouldShowRevocationToggle = useMemo(() => {
        const selectedRowKeys = fleetManagerState.selectedRowKeys;

        if (selectedRowKeys.length === 0) {
            return false;
        }

        if (isEntryRightsMyProductsRowKeyArray(selectedRowKeys)) {
            // if any of the selected row key have any product then there are products to revoke
            return selectedRowKeys.some(key => !!key.entryRightId);
        }

        const selectedIds = new Set<string>(selectedRowKeys);

        if (!parkingDelegees.pages) {
            return false;
        }

        return Object.values(parkingDelegees.pages).some(page =>
            (page.data ?? []).some(d =>
                selectedIds.has(d.registrationId) && (d.products?.length ?? 0) > 0
            )
        );
    }, [fleetManagerState.selectedRowKeys, productType, parkingDelegees.pages]);

    const closeAndClearSelection = () => {
        toggleBatchDeleteDelegationsWarning();
        setSelectedParkingRightIds([]);
    };

    const handleDeleteConfirmation = () => {
        const selectedRowKeys = fleetManagerState.selectedRowKeys;

        if (isEntryRightsMyProductsRowKeyArray(selectedRowKeys)) {
             dispatch(
                deleteEntryRightDelegees({
                    model: {
                        customerId: appState.user.seasonTicketOwnerCrmId!,
                        delegeeIds: selectedRowKeys.map(k => k.delegeeId),
                    },
                    delegeePmcIds: []
                }, () => closeAndClearSelection())
            );
            return;
         }
      
        dispatch(
            parkingProductsActions.deleteDelegations(
                {
                    registrationIds: fleetManagerState.selectedRowKeys,
                    seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId!,
                    sendRevocationConfirmationEmail: shouldShowRevocationToggle
                        ? sendRevocationConfirmationEmail
                        : undefined,
                 },
                () => {
                    onRefresh();
                    closeAndClearSelection();
                }
            )
        );
    };

    return (
        <Warning
            warning={options.warning}
            details={options.warningDescription}
            onCancel={toggleBatchDeleteDelegationsWarning}
            onConfirm={handleDeleteConfirmation}
            confirmText={t("delete")}
            children={shouldShowRevocationToggle && (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                    <qp-switch
                        data-size="s"
                        data-checked={sendRevocationConfirmationEmail}
                        onClick={() => setSendRevocationConfirmationEmail(!sendRevocationConfirmationEmail)}
                    />
                    <span>{options.revocationEmailText}</span>
                </div>
            )}
            disabled={loading}
        />
    );
};

export default BatchDeleteDelegeeWarning;
